import React, { useState, useEffect, useCallback } from 'react';
import { useDataCache } from '../context/DataCacheContext';
import { hybridCache } from '../utils/hybridCache';
import { smartCacheManager } from '../utils/smartCacheInvalidation';
// import { backgroundSyncManager } from '../utils/backgroundSync'; // REMOVED: Abandoned feature
import { createLogger } from '../utils/logger-ssr-safe';

const logger = createLogger({ module: 'CacheDebugPanel' });

interface CacheStats {
  legacyCache: {
    totalKeys: number;
    userKeys: number;
  };
  hybridCache: Record<string, unknown>;
  isHybridCacheEnabled: boolean;
}

interface HealthStatus {
  legacyCache: {
    available: boolean;
    totalKeys: number;
  };
  hybridCache: Record<string, unknown>;
  overall: boolean;
}

export const CacheDebugPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [testKey, setTestKey] = useState('debug-test-key');
  const [testValue, setTestValue] = useState('{"test": "data", "timestamp": "' + new Date().toISOString() + '"}');
  const [testResult, setTestResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [syncStatus, setSyncStatus] = useState<Record<string, unknown> | null>(null);
  const [offlineOpsCount, setOfflineOpsCount] = useState(0);
  const [syncResults, setSyncResults] = useState<{
    synced: number;
    failed: number;
    conflicts: number;
  } | null>(null);

  // IMPORTANT: All hooks must be called unconditionally at the top level
  // Call useDataCache without try-catch - if it throws, let error boundary handle it
  const {
    getCacheStats,
    healthCheck,
    getSyncStatus,
    forceSyncNow,
    getWithOfflineSupport,
    setWithOfflineSupport,
    syncOfflineOperations,
    getOfflineOperationsCount,
    clearOfflineOperations,
    setData,
    getDataAsync,
    invalidateCache,
    preloadData
  } = useDataCache();

  const refreshStats = useCallback(async (suppressConsoleLog = false) => {
    try {
      setIsLoading(true);
      const [cacheStats, health, sync] = await Promise.all([
        getCacheStats(),
        healthCheck(),
        getSyncStatus()
      ]);

      // Get offline operations count
      const opsCount = getOfflineOperationsCount();
      setOfflineOpsCount(opsCount);

      setStats(cacheStats as unknown as CacheStats);
      setHealthStatus(health as HealthStatus);
      setSyncStatus(sync as Record<string, unknown>);

      if (!suppressConsoleLog) {
        console.log('Cache Stats:', cacheStats);
        console.log('Health Status:', health);
        console.log('Sync Status:', sync);
      }
    } catch (error) {
      console.error('Failed to refresh stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getCacheStats, healthCheck, getSyncStatus, getOfflineOperationsCount]);

  useEffect(() => {
    if (isOpen && autoRefresh) {
      refreshStats();
      const interval = setInterval(refreshStats, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    } else if (isOpen) {
      // Just refresh once when becoming visible
      refreshStats();
    }
  }, [isOpen, autoRefresh, refreshStats]);

  const handleTestSet = async () => {
    setIsLoading(true);
    try {
      const data = JSON.parse(testValue);
      
      if (setData) {
        await setData(testKey, data, { useHybridCache: true, ttl: 60000 });
      } else {
        await hybridCache.set(testKey, data, 'debug-user', { ttl: 60000 });
      }
      
      setTestResult('✅ Data set successfully in hybrid cache');
      await refreshStats(true); // Suppress console log for test operations
    } catch (error) {
      setTestResult('❌ Failed to set data: ' + (error instanceof Error ? error.message : String(error)));
    }
    setIsLoading(false);
  };

  const handleTestGet = async () => {
    setIsLoading(true);
    try {
      let result;
      
      if (typeof setData === 'function') {
        result = await getDataAsync(testKey, { useHybridCache: true });
      } else {
        result = await hybridCache.get(testKey, 'debug-user');
      }
      
      setTestResult(`✅ Retrieved data: ${JSON.stringify({
        status: result.status,
        source: result.source,
        age: result.age,
        hasData: !!result.data,
        data: result.data
      }, null, 2)}`);
    } catch (error) {
      setTestResult('❌ Failed to get data: ' + (error instanceof Error ? error.message : String(error)));
    }
    setIsLoading(false);
  };

  const handleTestInvalidate = async () => {
    setIsLoading(true);
    try {
      if (typeof setData === 'function') {
        await invalidateCache(testKey);
      } else {
        await hybridCache.invalidate(testKey, 'debug-user');
      }
      
      setTestResult('✅ Cache key invalidated successfully');
      await refreshStats(true); // Suppress console log for test operations
    } catch (error) {
      setTestResult('❌ Failed to invalidate: ' + (error instanceof Error ? error.message : String(error)));
    }
    setIsLoading(false);
  };

  const handleTestPreload = async () => {
    setIsLoading(true);
    try {
      let data;
      
      if (typeof setData === 'function') {
        data = await preloadData(
          testKey + '-preload',
          async () => {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            return { preloaded: true, timestamp: new Date().toISOString() };
          },
          { useHybridCache: true, ttl: 120000 }
        );
      } else {
        data = await hybridCache.preload(
          testKey + '-preload',
          'debug-user',
          async () => {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            return { preloaded: true, timestamp: new Date().toISOString() };
          },
          { ttl: 120000 }
        );
      }
      
      setTestResult(`✅ Data preloaded: ${JSON.stringify(data, null, 2)}`);
      await refreshStats(true); // Suppress console log for test operations
    } catch (error) {
      setTestResult('❌ Failed to preload: ' + (error instanceof Error ? error.message : String(error)));
    }
    setIsLoading(false);
  };

  const handleTestBackgroundSync = async () => {
    setIsLoading(true);
    try {
      // backgroundSyncManager removed - abandoned feature
      // If offline sync needed, use Service Workers or Cloudflare Queues
      setTestResult('⚠️ Background sync feature has been removed (abandoned feature)');
    } catch (error) {
      setTestResult(`❌ Background sync test failed: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceSyncNow = async () => {
    setIsLoading(true);
    try {
      await forceSyncNow();
      await refreshStats(true);
      setTestResult('✅ Force sync completed successfully');
    } catch (error) {
      setTestResult(`❌ Force sync failed: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSyncQueue = () => {
    try {
      // backgroundSyncManager removed - abandoned feature
      logger.debug('Clear sync queue requested but background sync not available');
      refreshStats(true);
      setTestResult('✅ Sync queue cleared successfully');
    } catch (error) {
      setTestResult(`❌ Failed to clear sync queue: ${error}`);
    }
  };

  const handleTestOfflineGet = async () => {
    setTestResult('Testing offline-first get...');
    try {
      const result = await getWithOfflineSupport(
        'test-offline-key',
        async () => {
          // Simulate network fetch
          await new Promise(resolve => setTimeout(resolve, 100));
          return { message: 'Fresh data from network', timestamp: Date.now() };
        },
        {
          allowStale: true,
          maxStaleAge: 3600000, // 1 hour
          backgroundRefresh: true
        }
      );

      setTestResult(`✅ Offline get successful: ${JSON.stringify(result, null, 2)}`);
    } catch (error) {
      setTestResult(`❌ Offline get failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleTestOfflineSet = async () => {
    setTestResult('Testing offline-first set...');
    try {
      await setWithOfflineSupport(
        'test-offline-key',
        { message: 'Offline test data', timestamp: Date.now() },
        {
          syncToServer: true,
          table: 'test_table',
          operation: 'update',
          priority: 1,
          optimisticUpdate: true
        }
      );

      setTestResult('✅ Offline set successful');
      await refreshStats(true);
    } catch (error) {
      setTestResult(`❌ Offline set failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleSyncOfflineOps = async () => {
    setTestResult('Syncing offline operations...');
    try {
      const results = await syncOfflineOperations();
      setSyncResults(results);
      setTestResult(`✅ Sync completed: ${JSON.stringify(results, null, 2)}`);
      await refreshStats(true);
    } catch (error) {
      setTestResult(`❌ Sync failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleClearOfflineOps = () => {
    clearOfflineOperations();
    setOfflineOpsCount(0);
    setSyncResults(null);
    setTestResult('✅ Offline operations queue cleared');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors z-50"
        title="Open Cache Debug Panel"
      >
        🔧 Cache Debug
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-4 w-96 max-h-96 overflow-y-auto z-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cache Debug Panel</h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          ✕
        </button>
      </div>

      {/* Auto-refresh toggle */}
      <div className="mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">Auto-refresh stats</span>
        </label>
      </div>

      {/* Health Status */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Health Status</h4>
        {healthStatus ? (
          <div className="text-sm space-y-1">
            <div className={`flex items-center ${healthStatus.overall ? 'text-green-600' : 'text-red-600'}`}>
              <span className="mr-2">{healthStatus.overall ? '✅' : '❌'}</span>
              Overall: {healthStatus.overall ? 'Healthy' : 'Issues Detected'}
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Legacy Cache: {healthStatus.legacyCache.available ? '✅' : '❌'} ({healthStatus.legacyCache.totalKeys} keys)
            </div>
            {healthStatus.hybridCache && (
              <div className="text-gray-600 dark:text-gray-400">
                L1 Cache: {(healthStatus.hybridCache as Record<string, unknown>).l1Available ? '✅' : '❌'}
                {(healthStatus.hybridCache as Record<string, unknown>).l2Available !== undefined && (
                  <span> | L2 Cache: {(healthStatus.hybridCache as Record<string, unknown>).l2Available ? '✅' : '❌'}</span>
                )}
                {Boolean((healthStatus.hybridCache as Record<string, unknown>).l2Error) && (
                  <div className="text-xs text-gray-500 mt-1">
                    L2 Note: {String((healthStatus.hybridCache as Record<string, unknown>).l2Error)}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500">Loading...</div>
        )}
      </div>

      {/* Cache Statistics */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Cache Statistics</h4>
        {stats ? (
          <div className="text-sm space-y-1">
            <div className="text-gray-600 dark:text-gray-400">
              Hybrid Cache: {stats.isHybridCacheEnabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Legacy Keys: {stats.legacyCache.totalKeys} total, {stats.legacyCache.userKeys} user
            </div>
            {stats.hybridCache && !(stats.hybridCache as Record<string, unknown>).error && (
              <div className="text-gray-600 dark:text-gray-400">
                L1 Hit Rate: {((Number((stats.hybridCache as Record<string, unknown>).l1HitRate) || 0) * 100).toFixed(1)}%
                {(stats.hybridCache as Record<string, unknown>).l2HitRate !== undefined && (
                  <span> | L2 Hit Rate: {((Number((stats.hybridCache as Record<string, unknown>).l2HitRate) || 0) * 100).toFixed(1)}%</span>
                )}
              </div>
            )}
            <div className="text-gray-600 dark:text-gray-400">
              Dependencies: {Object.keys(smartCacheManager.getDependencyGraph()).length} registered
            </div>
          </div>
        ) : (
          <div className="text-gray-500">Loading...</div>
        )}
      </div>

      {/* Sync Status */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Background Sync</h4>
        {syncStatus ? (
          <div className="text-sm space-y-1">
            <div className="text-gray-600 dark:text-gray-400">
              Online: {syncStatus.isOnline ? '✅' : '❌'}
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Queue: {String(syncStatus.queueLength)} operations
            </div>
            <div className="text-gray-600 dark:text-gray-400">
              Processing: {syncStatus.isProcessing ? '🔄' : '⏸️'}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">No sync data available</div>
        )}
      </div>

      {/* Offline-First Cache Section */}
      <div className="mb-4">
        <h4 className="font-semibold mb-2">Offline-First Cache</h4>
        <div className="space-y-2">
          <div className="text-sm">
            <span className="font-medium">Offline Operations Queue:</span> {offlineOpsCount}
          </div>
          
          {syncResults && (
            <div className="text-sm">
              <span className="font-medium">Last Sync:</span>
              <div className="ml-2">
                ✅ Synced: {syncResults.synced}<br/>
                ❌ Failed: {syncResults.failed}<br/>
                ⚠️ Conflicts: {syncResults.conflicts}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1">
            <button
              onClick={handleTestOfflineGet}
              className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
            >
              Test Offline Get
            </button>
            <button
              onClick={handleTestOfflineSet}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Test Offline Set
            </button>
            <button
              onClick={handleSyncOfflineOps}
              className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
              disabled={offlineOpsCount === 0}
            >
              Sync Offline Ops
            </button>
            <button
              onClick={handleClearOfflineOps}
              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
              disabled={offlineOpsCount === 0}
            >
              Clear Queue
            </button>
          </div>
        </div>
      </div>

      {/* Test Controls */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Test Operations</h4>
        
        <div className="space-y-2">
          <div>
            <input
              type="text"
              value={testKey}
              onChange={(e) => setTestKey(e.target.value)}
              placeholder="Test key"
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <textarea
              value={testValue}
              onChange={(e) => setTestValue(e.target.value)}
              placeholder="Test value (JSON)"
              rows={2}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleTestSet}
              disabled={isLoading}
              className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Set
            </button>
            <button
              onClick={handleTestGet}
              disabled={isLoading}
              className="px-2 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              Get
            </button>
            <button
              onClick={handleTestPreload}
              disabled={isLoading}
              className="px-2 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            >
              Preload
            </button>
            <button
              onClick={handleTestInvalidate}
              disabled={isLoading}
              className="px-2 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
            >
              Invalidate
            </button>
          </div>
          <button
            onClick={handleTestBackgroundSync}
            disabled={isLoading}
            className="w-full px-2 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            Test Sync Queue
          </button>
          <button
            onClick={handleForceSyncNow}
            disabled={isLoading}
            className="w-full px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            Force Sync Now
          </button>
        </div>
      </div>

      {/* Sync Controls */}
      <div className="mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Sync Controls</h4>
        <div className="grid grid-cols-1 gap-2">
          <button
            onClick={handleClearSyncQueue}
            disabled={isLoading}
            className="w-full px-2 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
          >
            Clear Sync Queue
          </button>
          <button
            onClick={() => {
              const graph = smartCacheManager.getDependencyGraph();
              console.log('Cache Dependency Graph:', graph);
              setTestResult(`✅ Dependency graph logged to console:\n${JSON.stringify(graph, null, 2)}`);
            }}
            disabled={isLoading}
            className="w-full px-2 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          >
            Show Dependencies
          </button>
        </div>
      </div>

      {/* Test Results */}
      {testResult && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Test Result</h4>
          <pre className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">
            {testResult}
          </pre>
        </div>
      )}

      {/* Refresh Button */}
      <button
        onClick={() => refreshStats(!autoRefresh)}
        disabled={isLoading}
        className="w-full px-2 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
      >
        {isLoading ? 'Loading...' : 'Refresh Stats'}
      </button>
    </div>
  );
}; 